import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '../hooks/useAuthStore';
import {
  LayoutGrid,
  X,
  Menu,
  Home,
  Search,
  Tag,
  ClipboardCheck,
  Grid3X3,
  Truck,
  Package,
  ScanLine,
  ClipboardList,
  PackageCheck,
  ListChecks,
  Factory,
  BarChart3,
  Users,
  Flame,
  Droplets,
  PackageOpen,
  Calculator,
  ShoppingBag,
  Handshake,
  Building2,
  GraduationCap,
  Calendar,
  BookOpen,
  Layers,
  FileText,
  TrendingUp,
  Settings,
  LogOut,
  Zap,
  ChevronRight,
  ChevronDown,
  FlaskConical,
  Printer,
  Wrench,
  ShoppingCart,
  Hammer,
  Scale,
  Mail,
} from 'lucide-react';
import { usePublishedTools } from '../hooks/usePublishedTools';
import HubActions from './HubActions';
import GlobalSearch from './GlobalSearch';
import './Navigation.css';

// Routes that use full-screen mode (hidden nav, hamburger only)
const STATION_ROUTES = [
  '/production/',
  '/picking-station',
  '/pick-tickets',
  '/pack-station',
  '/pending-fulfillment/vertical',
  '/ops/',
  '/delivery/driver',
  '/track/',
];

function isStationRoute(pathname) {
  // Exact /ops is NOT a station route (that's the landing page)
  if (pathname === '/ops') return false;
  return STATION_ROUTES.some((r) => pathname.startsWith(r));
}

// The Railway API host (1001) also serves a copy of the site, and it can lag the
// Vercel one at run.daydreamerconcepts.com. When the page is open on 1001, nav links
// go to the Vercel site so stations land on the current build.
const ON_API_HOST = typeof window !== 'undefined' && window.location.hostname.startsWith('1001.');
const RUN_SITE = 'https://run.daydreamerconcepts.com';

function Link({ to, onClick, ...props }) {
  if (ON_API_HOST) return <a href={`${RUN_SITE}${to}`} onClick={onClick} {...props} />;
  return <RouterLink to={to} onClick={onClick} {...props} />;
}

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  // Desktop top-bar dropdown currently open (section index), or null.
  const [openMenu, setOpenMenu] = useState(null);
  // Viewport position of the open dropdown, computed from its trigger button.
  // The dropdown is position:fixed so it isn't clipped by nav-topbar-menus'
  // overflow-x:auto (which forces overflow-y to auto too, per the CSS spec,
  // hiding anything that pops out below a horizontally-scrolling ancestor).
  const [dropdownPos, setDropdownPos] = useState(null);
  const menuBtnRefs = useRef({});

  const openMenuAt = (idx) => {
    const el = menuBtnRefs.current[idx];
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropdownPos({ left: rect.left, top: rect.bottom });
    }
    setOpenMenu(idx);
  };
  const location = useLocation();
  const { user, signOut } = useAuthStore();
  const teamTools = usePublishedTools();
  const stationMode = isStationRoute(location.pathname);
  // Wall boards (?kiosk=1) are read-only and nobody is standing at them, so the menu button
  // and search are dead pixels. Opt-in per URL rather than per route: the other station
  // screens (wicking, carting) are touched by operators and DO need the hamburger.
  const kioskMode = new URLSearchParams(location.search).get('kiosk') === '1';

  const isActive = (path) => {
    if (path === '/ops') return location.pathname === '/ops';
    const base = String(path).split('?')[0]; // record rows carry ?tag= params
    return location.pathname === base || location.pathname.startsWith(base + '/');
  };

  const navSections = useMemo(() => [
    {
      title: 'Operations',
      icon: Home,
      items: [
        { path: '/ops', icon: Home, label: 'Dashboard' },
        { path: '/labels', icon: Tag, label: 'Label Printer' },
        { path: '/bin-labels', icon: Grid3X3, label: 'Bin Label Printer' },
        { path: '/price-stickers', icon: Tag, label: 'Price Sticker Printer' },
        { path: '/key-account-po', icon: ClipboardCheck, label: 'Key Account PO' },
        { path: '/bn-shipping-labels', icon: Tag, label: 'B&N Shipping Labels' },
        { path: '/goods-receipt', icon: PackageOpen, label: 'Goods Receipt' },
        { path: '/shopify/rewined/orders', icon: ShoppingBag, label: 'Shopify Orders' },
        { path: '/native-work-orders/suggestions', icon: Factory, label: 'Suggested Builds (Native)' },
        { path: '/native-items/bin', icon: ScanLine, label: 'Bin Lookup (Native)' },
      ]
    },
    {
      // NetSuite-style record types: the row opens the list; hover shows
      // New | List chips. newPath appears as native create screens land.
      title: 'Records',
      icon: FileText,
      items: [
        { path: '/records', icon: Search, label: 'Search All Records' },
        { path: '/native-items', icon: Package, label: 'Items', newPath: '/native-items/new' },
        { path: '/records?tag=so:', icon: FileText, label: 'Sales Orders', newPath: '/native-orders/new' },
        { path: '/records?tag=po:', icon: ShoppingCart, label: 'Purchase Orders', newPath: '/native-purchase-orders/new' },
        { path: '/records?tag=wo:', icon: Hammer, label: 'Work Orders', newPath: '/native-work-orders/new' },
        { path: '/native-inventory-adjustments', icon: Scale, label: 'Inventory Adjustments', newPath: '/native-inventory-adjustments/new' },
        { path: '/native-assembly-builds', icon: Hammer, label: 'Assembly Builds', newPath: '/native-assembly-builds/new' },
        { path: '/native-transfers', icon: Truck, label: 'Inventory Transfers', newPath: '/native-transfers/new' },
        { path: '/native-fulfillments', icon: PackageCheck, label: 'Fulfillments (Native)', newPath: '/native-fulfillments/new' },
        { path: '/native-receipts', icon: PackageOpen, label: 'Goods Receipts (Native)', newPath: '/native-receipts/new' },
        { path: '/native-payments', icon: Calculator, label: 'Customer Payments', newPath: '/native-payments/new' },
        { path: '/native-invoices', icon: FileText, label: 'Invoices', newPath: '/native-invoices/new' },
        { path: '/native-credit-memos', icon: FileText, label: 'Credit Memos', newPath: '/native-invoices/new' },
        { path: '/records/estimates', icon: FileText, label: 'Estimates', newPath: '/records/estimates/new' },
        { path: '/records/return-authorizations', icon: PackageOpen, label: 'Return Authorizations', newPath: '/records/return-authorizations/new' },
        { path: '/records?tag=cust:', icon: Users, label: 'Customers', newPath: '/records/customers/new' },
        { path: '/records?tag=ven:', icon: Users, label: 'Vendors', newPath: '/records/vendors/new' },
        { path: '/records/outbox', icon: Mail, label: 'Documents Outbox' },
      ]
    },
    {
      title: 'Fulfillment',
      icon: ScanLine,
      items: [
        { path: '/fulfillment/quick-shipment', icon: Zap, label: 'Quick Shipment' },
        { path: '/fulfillment/market-shipments', icon: ShoppingBag, label: 'Candlefish CHS Store Shipments' },
        { path: '/fulfillment/store-box', icon: Package, label: 'Pack Store Box (King St)' },
        { path: '/fulfillment', icon: ScanLine, label: 'Fulfillment Scanner' },
        { path: '/pending-fulfillment', icon: ClipboardList, label: 'Pending Fulfillment' },
        { path: '/picking-station', icon: ListChecks, label: 'Picking Station' },
        { path: '/pick-tickets', icon: Printer, label: 'Pick Tickets' },
        { path: '/pack-station', icon: PackageCheck, label: 'Pack Station' },
        { path: '/fulfillment/list', icon: Truck, label: 'Fulfillment List' },
      ]
    },
    {
      title: 'Local Delivery',
      icon: Truck,
      items: [
        { path: '/delivery/dispatch', icon: ClipboardList, label: 'Dispatch' },
        { path: '/delivery/driver',   icon: Truck,         label: 'Driver App' },
      ]
    },
    {
      title: 'Production',
      icon: Factory,
      items: [
        { path: '/workorders', icon: FileText, label: 'Work Orders' },
        { path: '/native-work-orders/new', icon: Hammer, label: 'New Work Order (Native)' },
        { path: '/production/dashboard', icon: Factory, label: 'Dashboard' },
        { path: '/production/stations', icon: Layers, label: 'By Station' },
        { path: '/production/operators', icon: Users, label: 'Operator Activity' },
        { path: '/production/carting', icon: Package, label: 'Carting' },
        { path: '/production/wicking', icon: Droplets, label: 'Wicking' },
        { path: '/production/pouring', icon: Flame, label: 'Pouring' },
        { path: '/production/packaging', icon: PackageOpen, label: 'Packaging' },
        { path: '/wick-testing', icon: FlaskConical, label: 'Wick Testing' },
        { path: '/station/key-account-check', icon: ClipboardCheck, label: 'Key Account Check Station' },
        { path: '/material-planning', icon: Calculator, label: 'Material Planning' },
        { path: '/ordering', icon: Package, label: 'Ordering' },
      ]
    },
    {
      title: 'Channels',
      icon: ShoppingBag,
      items: [
        { path: '/amazon', icon: Truck, label: 'Amazon FBA' },
        { path: '/faire', icon: Handshake, label: 'Faire' },
        { path: '/national-accounts', icon: Building2, label: 'National Accounts' },
        { path: '/foxtrot', icon: ShoppingBag, label: 'Foxtrot Orders' },
        { path: '/workshops', icon: GraduationCap, label: 'Workshops' },
        { path: '/workshops/calendar', icon: Calendar, label: 'Calendar' },
        { path: '/workshops/bookings', icon: BookOpen, label: 'Bookings' },
        { path: '/workshops/sessions', icon: Layers, label: 'Sessions' },
        { path: '/workshops/reports', icon: BarChart3, label: 'Workshop Reports' },
        { path: '/market-research', icon: TrendingUp, label: 'Market Research' },
      ]
    },
    {
      title: 'Experiments',
      icon: FlaskConical,
      items: [
        { path: '/experiments', icon: FlaskConical, label: 'Dashboard' },
        { path: '/experiments/new', icon: FlaskConical, label: 'New Experiment' },
        { path: '/experiments/flags', icon: FlaskConical, label: 'Feature Flags' },
      ]
    },
    // Tools teammates built and published into Run (approved via the Hub)
    ...(teamTools.length > 0 ? [{
      title: 'Team Tools',
      icon: Wrench,
      items: teamTools.map((t) => ({ path: `/tools/${t.id}`, icon: Wrench, label: t.name })),
    }] : []),
    {
      title: 'Settings',
      icon: Settings,
      items: [
        { path: '/settings', icon: Settings, label: 'Settings' },
      ]
    },
  ], [teamTools]);

  // Determine which section contains the active route
  const activeSectionIndex = useMemo(() => {
    for (let i = 0; i < navSections.length; i++) {
      if (navSections[i].items.some((item) => isActive(item.path))) {
        return i;
      }
    }
    return 0;
  }, [location.pathname, navSections]);

  const [expandedSections, setExpandedSections] = useState(() => new Set([activeSectionIndex]));

  // Auto-expand section when active route changes
  useEffect(() => {
    setExpandedSections((prev) => {
      if (prev.has(activeSectionIndex)) return prev;
      const next = new Set(prev);
      next.add(activeSectionIndex);
      return next;
    });
  }, [activeSectionIndex]);

  // Close any open top-bar dropdown on route change.
  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  const toggleSection = (idx) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  // Placed after every hook above so hook order stays unconditional.
  if (kioskMode) return null;

  return (
    <>
      {/* Menu button - mobile always, desktop only on station routes */}
      <header className={`nav-top-header ${stationMode ? 'station-mode' : ''}`}>
        <button
          className="nav-menu-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Open navigation"
        >
          <Menu size={24} />
        </button>
        {/* Station screens: the header is a fixed transparent overlay, so the search
            box would sit on top of page content (e.g. pick-ticket stat cards). The
            drawer's sidebar search still covers those screens. */}
        {!stationMode && <GlobalSearch variant="header" />}
      </header>

      {/* Desktop: NetSuite-style top bar — major categories with dropdowns.
          Hidden below 1024px (mobile keeps the hamburger drawer) and on
          station routes (full-screen floor tools keep their overlay). */}
      {!stationMode && (
        <nav className="nav-topbar" onMouseLeave={() => setOpenMenu(null)}>
          <div className="nav-topbar-left">
            <HubActions />
            <Link to="/ops" className="nav-topbar-brand" onClick={() => setOpenMenu(null)}>
              Run
            </Link>
          </div>
          <div className="nav-topbar-menus">
            {navSections.map((section, sIdx) => {
              const SectionIcon = section.icon;
              const sectionHasActive = section.items.some((item) => isActive(item.path));
              const open = openMenu === sIdx;
              return (
                <div
                  key={sIdx}
                  className={`nav-topbar-menu ${open ? 'open' : ''} ${sectionHasActive ? 'has-active' : ''}`}
                  onMouseEnter={() => openMenuAt(sIdx)}
                >
                  <button
                    ref={(el) => { menuBtnRefs.current[sIdx] = el; }}
                    className="nav-topbar-menu-btn"
                    onClick={() => openMenuAt(sIdx)}
                    aria-expanded={open}
                  >
                    <SectionIcon size={15} className="nav-topbar-menu-icon" />
                    <span>{section.title}</span>
                    <ChevronDown size={13} className="nav-topbar-menu-chevron" />
                  </button>
                  {open && dropdownPos && (
                    <div
                      className="nav-topbar-dropdown"
                      style={{
                        left: dropdownPos.left,
                        top: dropdownPos.top,
                        maxHeight: `calc(100vh - ${dropdownPos.top}px - 12px)`,
                      }}
                    >
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        if (item.newPath) {
                          return (
                            <div
                              key={item.path}
                              className={`nav-link nav-record-row ${isActive(item.path) || isActive(item.newPath) ? 'active' : ''}`}
                            >
                              <Link to={item.path} onClick={() => setOpenMenu(null)} className="nav-record-main">
                                <Icon size={16} className="nav-link-icon" />
                                <span className="nav-link-label">{item.label}</span>
                              </Link>
                              <span className="nav-record-actions">
                                <Link to={item.newPath} onClick={() => setOpenMenu(null)} className="nav-record-chip" title={`New ${item.label}`}>New</Link>
                                <Link to={item.path} onClick={() => setOpenMenu(null)} className="nav-record-chip" title={`${item.label} list`}>List</Link>
                              </span>
                            </div>
                          );
                        }
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setOpenMenu(null)}
                            className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                          >
                            <Icon size={16} className="nav-link-icon" />
                            <span className="nav-link-label">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="nav-topbar-right">
            <GlobalSearch variant="header" />
            {user && (
              <button className="nav-logout-btn" onClick={signOut} title={`Sign out ${user.email}`}>
                <LogOut size={16} />
              </button>
            )}
          </div>
        </nav>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div className={`nav-overlay ${stationMode ? 'station-mode' : ''}`} onClick={() => setIsOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`nav-sidebar ${isOpen ? 'open' : ''} ${stationMode ? 'station-mode' : ''}`}>
        {/* Sidebar header */}
        <div className="nav-sidebar-header">
          <div className="nav-sidebar-header-left">
            <HubActions />
            <div className="nav-header-text">
              <h1 className="nav-app-title">Run</h1>
              <p className="nav-app-subtitle">Operations</p>
            </div>
          </div>
          <button
            className="nav-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
        </div>

        {/* Global search — inline box, results drop down as you type (task #64) */}
        <GlobalSearch variant="sidebar" />

        {/* Navigation links */}
        <div className="nav-content">
          {navSections.map((section, sIdx) => {
            const SectionIcon = section.icon;
            const isSectionExpanded = expandedSections.has(sIdx);
            const sectionHasActive = section.items.some((item) => isActive(item.path));

            return (
              <div key={sIdx} className={`nav-section ${sectionHasActive ? 'has-active' : ''}`}>
                {/* Section header - clickable to toggle */}
                <button
                  className={`nav-section-header ${isSectionExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleSection(sIdx)}
                >
                  <SectionIcon size={18} className="nav-section-icon" />
                  <span className="nav-section-title">{section.title}</span>
                  <ChevronRight size={14} className={`nav-section-chevron ${isSectionExpanded ? 'rotated' : ''}`} />
                </button>

                {/* Items - collapsible */}
                <div className={`nav-section-items ${isSectionExpanded ? 'expanded' : ''}`}>
                  <nav className="nav-list">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        // Record-type rows (NetSuite pattern): the row itself
                        // opens the LIST; hovering reveals New | List chips.
                        if (item.newPath) {
                          return (
                            <div key={item.path} className={`nav-link nav-record-row ${isActive(item.path) || isActive(item.newPath) ? 'active' : ''}`}>
                              <Link to={item.path} onClick={() => setIsOpen(false)} className="nav-record-main">
                                <Icon size={18} className="nav-link-icon" />
                                <span className="nav-link-label">{item.label}</span>
                              </Link>
                              <span className="nav-record-actions">
                                <Link to={item.newPath} onClick={() => setIsOpen(false)} className="nav-record-chip" title={`New ${item.label}`}>New</Link>
                                <Link to={item.path} onClick={() => setIsOpen(false)} className="nav-record-chip" title={`${item.label} list`}>List</Link>
                              </span>
                            </div>
                          );
                        }
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                          >
                            <Icon size={18} className="nav-link-icon" />
                            <span className="nav-link-label">{item.label}</span>
                          </Link>
                        );
                      })}
                  </nav>
                </div>

              </div>
            );
          })}
        </div>

        {/* Footer */}
        {user && (
          <div className="nav-footer">
            <span className="nav-user-email">{user.email}</span>
            <button className="nav-logout-btn" onClick={signOut} title="Sign Out">
              <LogOut size={16} />
            </button>
          </div>
        )}
      </aside>

    </>
  );
}

export default Navigation;
